import { Injectable, } from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { ToasterNotificationService } from "./toaster-notification.service";
import { Observable, map, catchError, Subject } from "rxjs";
import { AIChatModel } from "../models/aiChat/ai-chat.model";
import { environment } from "src/environments/environment";
import { ChatCompletionRequestModel } from "../models/aiChat/chat-completion-request.model";
import { RagChatCompletionRequestModel } from "../models/aiChat/rag-chat-completion-request.model";
import { BaseApiService } from "./base-api.service";
import oboe, { Oboe } from "oboe";
import { AuthMyfileService } from "./auth.myfile.service";
import { ChatStreamingModel } from "../models/aiChat/chat-streaming.model";
import { oboeConfigModel } from "../models/aiChat/oboe-config.model.";
import { ChatRagStreamingModel } from "../models/aiChat/chat-rag-streaming.model";
import { RagEntityFileRequestModel } from "../models/aiChat/rag-entity-file-request.model";
import { OCRCreateColorHighlightedKeywordFileRequestModel } from "../models/file/ocr-create-color-highlighted-keyword-file-request-model";
import { FileRequestModel } from "../models/file/file-request-model";
import { BlogsBaseModel } from "../models/blogs/blogs-base.model";
import { CategoryModel } from "../models/document/category-model";
import { CategorySearchModel } from "../models/document/category-search-model";
import { ErrorModel } from "../models/error.model";
import { BaseSearchModel } from "../models/query/base-search.model";
import { SearchResultModel } from "../models/query/search-result-model";
import { TaskSearchModel } from "../models/task/task-search.model";
import { TaskModel } from "../models/task/task.model";
import { PreferenceModel } from "../models/preference/preference-model";
import { TableConfigurationModel } from "../models/table/table-configuration.model";
import { TableConfigSearchModel } from "../models/table/searchModels/table-config-search.model";
import { PaymentModel } from "../models/commerce/payment.model";
import { CreditCardPaymentInfoModel } from "../models/members/user/credit-card-payment-info-model";
import { PaymentSearchModel } from "../models/commerce/payment-search.model";

const AI_API_URL = `${environment.aiApiUrl}`;

let statusCode200: boolean = true;

@Injectable({
    providedIn: 'root'
})
export class AiApiService extends BaseApiService {

    private oboeReader: Oboe | undefined = undefined;

    private get authorization$() { return `Bearer ${this.authMyfileService.getAuthFromLocalStorage()?.jwtAccessToken?.accessToken}`; }

    private config = new oboeConfigModel();

    constructor(
        private http: HttpClient,
        private authMyfileService: AuthMyfileService,
        override toasterNotificationService: ToasterNotificationService
    ) {
        super(toasterNotificationService);
    }

    postAiChat(chatModel: ChatCompletionRequestModel): Observable<AIChatModel | undefined> {
        return this.http.post(AI_API_URL + `/AIServices/chat`, chatModel, { observe: 'response' })
            .pipe(
                map((result: any) => {
                    if (result.status == 200) {
                        return result.body?.valueOf()
                    }
                    return undefined;
                }),
                catchError(err => this.handleError(err))
            );
    }

    postAiChatRag(chatModel: RagChatCompletionRequestModel): Observable<AIChatModel | undefined> {

        return this.http.post(AI_API_URL + `/AIServices/chat/rag`, chatModel, { observe: 'response' })
            .pipe(
                map((result: any) => {
                    if (result.status == 200) {
                        return result.body?.valueOf()
                    }
                    return undefined;
                }),
                catchError(err => this.handleError(err))
            );
    }

    setLikeStatus(chatModel: AIChatModel): Observable<AIChatModel | undefined> {

        return this.http.put(AI_API_URL + `/AIServices/aichat/${chatModel.id}/like/${chatModel.liked}`, chatModel, { observe: 'response' })
            .pipe(
                map((result: any) => {
                    if (result.status == 200) {
                        console.log('SUCCESS')
                    }
                    return undefined;
                }),
                catchError(err => this.handleError(err))
            );
    }

    abortReader() {
        if (this.oboeReader !== undefined) {
            this.oboeReader.abort();
        }
    }

    chatKnowledgeBaseLoad(ragEntityFileRequestModel: RagEntityFileRequestModel) {
        return this.http.post(AI_API_URL + `/AIServices/knowledgebase/load`, ragEntityFileRequestModel, { observe: 'response' })
            .pipe(
                map((result: any) => {
                    if (result.status == 200) {
                        return result.body?.valueOf()
                    }
                    return undefined;
                }),
                catchError((err: any) => this.handleError(err))
            );

    }

    chatKnowledgeBaseDelete(ragEntityFileRequestModel: RagEntityFileRequestModel) {
        return this.vectorStorageDelete(ragEntityFileRequestModel);
    }

    vectorStorageDelete(ragEntityFileRequestModel: RagEntityFileRequestModel) {
        const url = `${AI_API_URL}/AIServices/delete`;
        return this.http.request('DELETE', url, { body: ragEntityFileRequestModel, observe: 'response' })
            .pipe(
                map((result: any) => {
                    if (result.status == 200) {
                        return result.body?.valueOf()
                    }
                    return undefined;
                }),
                catchError(err => this.handleError(err))
            );
    }

    checkForErrorCode(err: any) {
        if (err) {
            statusCode200 = false;
        }
    }

    postChatRagStreaming(chatRagStreamingModel: ChatRagStreamingModel): Observable<any> {

        var sourceText = new Subject<any>();
        var sourceText$ = sourceText.asObservable();

        //need custom configs for the oboe to work. 

        this.config.url = `${AI_API_URL}/AIServices/chat/rag/streaming`;
        this.config.method = `POST`;
        this.config.headers.Authorization = this.authorization$;
        this.config.body = chatRagStreamingModel;

        statusCode200 = true;

        this.abortReader()
        this.oboeReader = oboe(this.config).node('Message', this.checkForErrorCode)

            .node('*', (result: string) => {

                if (statusCode200) {
                    sourceText.next(`${result}`)
                }

            }).done(function (result: any) {
                sourceText.next(true)
            }).fail(function (result: any) {
                if (statusCode200) {
                    sourceText.next(false)
                } else {
                    sourceText.next(null)
                }
            });

        return sourceText$;
    }

    postChatStreaming(chatStreamingModel: ChatStreamingModel): Observable<any> {

        var sourceText = new Subject<any>();
        var sourceText$ = sourceText.asObservable();

        //need custom configs for the oboe to work. 

        this.config.url = `${AI_API_URL}/AIServices/chat/streaming`;
        this.config.method = `POST`;
        this.config.headers.Authorization = this.authorization$;
        this.config.body = chatStreamingModel;

        this.abortReader()
        this.oboeReader = oboe(this.config).node('*', function (result: any) {
            sourceText.next(`${result}`)
        }).done(function (result: any) {
            sourceText.next(true);
        }).fail(function (result: any) {
            // on failure code goes here
            sourceText.next(false);
        });

        return sourceText$;
    }

    getAsync(): Observable<any> {

        var sourceText = new Subject<any>();
        var sourceText$ = sourceText.asObservable();

        //need custom configs for the oboe to work. 
        this.config.url = `${AI_API_URL}/AIServices/hello-get-async`;
        this.config.method = `GET`;
        this.config.headers.Authorization = this.authorization$;
        this.abortReader()
        this.oboeReader = oboe(this.config).node('*', function (result: any) {
            sourceText.next(`${result}`)
        }).done(function (result: any) {
            sourceText.next(`Done: ${result}`)
            sourceText.next(true)
        }).fail(function (result: any) {
            // on failure code goes here
            console.log(false);
        });

        return sourceText$;
    }

    createColorHighlightedSearchTermsPDF(ocrModel: OCRCreateColorHighlightedKeywordFileRequestModel): Observable<boolean | undefined> {
        const url = `${AI_API_URL}/OCRServices/create-color-highlighted-search-terms-pdf`;
        return this.http.post<boolean>(url, ocrModel, { observe: 'response' })
            .pipe(
                map(data => {
                    if (data.status == 200) {
                        return data.body?.valueOf()
                    }
                    return false;
                }),
                catchError(err => this.handleError(err))
            );
    }

    processOcr(ocrModel: FileRequestModel): Observable<boolean | undefined> {
        const url = `${AI_API_URL}/OCRServices/ocr`;
        return this.http.post<boolean>(url, ocrModel, { observe: 'response' })
            .pipe(
                map(data => {
                    if (data.status == 200) {
                        return data.body?.valueOf()
                    }
                    return false;
                }),
                catchError(err => this.handleError(err))
            );
    }

    getTasks(search: TaskSearchModel): Observable<SearchResultModel<TaskModel> | undefined> {
        const url = `${AI_API_URL}/Tasks`;
        return this.http.post<SearchResultModel<TaskModel> | undefined>(url, JSON.stringify(search)).pipe(
            catchError(err => this.handleError(err))
        );
    }

    newTask(): Observable<TaskModel | undefined> {
        const url = `${AI_API_URL}/Tasks/new`;
        return this.http.post<TaskModel | undefined>(url, { observe: 'response' }).pipe(
            map(data => {
                if (data && data.id) {
                    return data;
                }
                this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
                return undefined;
            }),
            catchError(err => this.handleError(err))
        );
    }

    newTaskWithModel(taskModel: TaskModel): Observable<TaskModel | undefined> {
        const url = `${AI_API_URL}/Tasks/new`;
        return this.http.post<TaskModel | undefined>(url, JSON.stringify(taskModel)).pipe(
            map(data => {
                if (data && data.id) {
                    return data;
                }
                this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
                return undefined;
            }),
            catchError(err => this.handleError(err))
        );
    }

    updateTask(taskModel: TaskModel): Observable<ErrorModel | boolean | undefined> {
        const url = `${AI_API_URL}/Tasks/${taskModel.id}`;
        return this.http.put<ErrorModel | boolean>(url, JSON.stringify(taskModel), { observe: 'response' })
            .pipe(
                map(data => {
                    if (data && data.status == 200) {
                        return true;
                    }
                    return false;
                }),
                catchError(err => this.handleError(err))
            );
    }

    executeTask(taskId: string): Observable<boolean | undefined> {
        const url = `${AI_API_URL}/Tasks/${taskId}/execute/1`;
        return this.http.get<ErrorModel | boolean>(url)
            .pipe(
                map(data => {
                    if (data) {
                        return true;
                    }
                    this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
                    return false;
                }),
                catchError(err => this.handleError(err)) //updated to use regular error handling now  - return undefined here instead of catch to avoid sending an error toaster message, when response is "Okay"
            );
    }

    getBlogs(search: BaseSearchModel): Observable<any> {
        const url = `${AI_API_URL}/Blogs`;

        return this.http.post<SearchResultModel<BlogsBaseModel>>(url, JSON.stringify(search));
    }

    getBlog(blogId: string) {
        const url = `${AI_API_URL}/Blogs/${blogId}`;

        return this.http.get<BlogsBaseModel>(url);
    }

    //Retrun is set to always be of(true) for now for testing and to send a request to server to see response with correct models. 
    updateBlog(blog: BlogsBaseModel): Observable<ErrorModel | boolean | undefined> {
        const url = `${AI_API_URL}/Blogs/${blog.id}`;

        return this.http.put<ErrorModel | boolean>(url, JSON.stringify(blog), { observe: 'response' })
            .pipe(
                map(data => {
                    if (data.status == 200) {
                        return true;
                    }
                    if (data.status == 204) {
                        return true;
                    }
                    return false;
                }),
                catchError(err => this.handleError(err))
            )
    }

    deleteBlog(blogId: string): Observable<ErrorModel | boolean | undefined> {
        const url = `${AI_API_URL}/Blogs/${blogId}`;

        return this.http.delete<ErrorModel | boolean>(url, { observe: 'response' })
            .pipe(
                map(data => {
                    if (data && data.status == 200) {
                        return true;
                    }
                    this.toasterNotificationService.showError($localize`:@@Toast_Error_GeneralText:Something went wrong. Please try again later.`, $localize`:@@Toast_Error_GeneralTitle:Error`);
                    return false;
                }),
                catchError(err => this.returnTrue(err))
                // catchError(err => this.handleError(err))
            );
    }

    createBlog(blog: BlogsBaseModel): Observable<BlogsBaseModel | undefined> {
        const url = `${AI_API_URL}/Blogs/new`;

        return this.http.post<BlogsBaseModel>(url, JSON.stringify(blog)).pipe(
            map((result: BlogsBaseModel) => {
                return result;
            }),
            catchError(err => this.handleError(err))
        );
    }

    getBlogTags(search: CategorySearchModel): Observable<SearchResultModel<CategoryModel> | undefined> {
        const url = `${AI_API_URL}/Blogs/tags`;

        return this.http.post<SearchResultModel<CategoryModel>>(url, JSON.stringify(search))
            .pipe(
                catchError(err => this.handleError(err))
            )
    }

    getBlogCategories(search: CategorySearchModel): Observable<SearchResultModel<CategoryModel> | undefined> {
        const url = `${AI_API_URL}/Blogs/categories`;
        return this.http.post<SearchResultModel<CategoryModel>>(url, JSON.stringify(search))
            .pipe(
                catchError(err => this.handleError(err))
            )
    }

    getUserPreferences(): Observable<PreferenceModel> {
        const url = `${AI_API_URL}/preferences`;
        return this.http.get<PreferenceModel>(url);
    }

    updateUserPreference(prefs: PreferenceModel): Observable<boolean> {
        const url = `${AI_API_URL}/preferences`;
        return this.http.put<boolean>(url, JSON.stringify(prefs), { observe: 'response' })
            .pipe(
                map(data => {
                    if (data.status == 200) {
                        return true;
                    }
                    return false;
                })
            );
    }

    createNewTableConfig(configModel: TableConfigurationModel): Observable<TableConfigurationModel | undefined> {
        const url = `${AI_API_URL}/TableConfig/new`;

        return this.http.post<TableConfigurationModel>(url, JSON.stringify(configModel)).pipe(
            map((result: TableConfigurationModel) => {
                return result;
            }),
            catchError(err => this.handleError(err))
        );
    }

    getTableConfig(search: TableConfigSearchModel): Observable<SearchResultModel<TableConfigurationModel> | undefined> {
        const url = `${AI_API_URL}/TableConfig`;

        return this.http.post<SearchResultModel<TableConfigurationModel>>(url, JSON.stringify(search))
            .pipe(
                catchError(err => this.handleError(err))
            )
    }

    updateTableConfig(configModel: TableConfigurationModel): Observable<boolean> {
        const url = `${AI_API_URL}/TableConfig`;

        return this.http.put<boolean>(url, JSON.stringify(configModel), { observe: 'response' })
            .pipe(
                map(data => {
                    if (data.status == 200) {
                        return true;
                    }
                    if (data.status == 204) {
                        return true;
                    }
                    return false;
                })
            )
    }

    createStripeToken(ccPaymentInfo: CreditCardPaymentInfoModel) {
        const url = `${AI_API_URL}/Payments/token`;
        return this.http.post<any>(url, JSON.stringify(ccPaymentInfo), { observe: 'response' })
            .pipe(
                map(data => {
                    return data.body?.valueOf()
                }),
                catchError(err => this.handleError(err))
            )
    }

    createPayment(payment: PaymentModel) {
        const url = `${AI_API_URL}/Payments`;
        return this.http.post<any>(url, JSON.stringify(payment), { observe: 'response' })
            .pipe(
                map(data => {
                    return data.body?.valueOf()
                }),
                catchError(err => this.handleError(err))
            )
    }

    getPayment(paymentId: string): Observable<PaymentModel | undefined> {
        var url = `${AI_API_URL}/Payments/${paymentId}"`
        return this.http.get<PaymentModel>(url)
            .pipe(
                catchError(err => this.handleError(err))
            );
    }

    getPaymentToken(userId: string) {
        const url = `${AI_API_URL}/Payments/token/user/${userId}`;
        return this.http.get<any>(url).pipe(
            map((data) => {
                return data;
            }),
            catchError(err => this.handleError(err)));
    }

    getPaymentList(search: PaymentSearchModel): Observable<SearchResultModel<PaymentModel> | undefined> {
        const url = `${AI_API_URL}/payments/search`;
        return this.http.post<SearchResultModel<PaymentModel>>(url, JSON.stringify(search))
            .pipe(
                catchError(err => this.handleError(err))
            );
    }

    refundPayment(paymentId: string, refundAmount: string): Observable<PaymentModel | undefined> {
        var url = `${AI_API_URL}/Payments/${paymentId}/refund/${refundAmount}`
        return this.http.get<PaymentModel>(url)
            .pipe(
                catchError(err => this.handleError(err))
            );
    }
}